import { Type, type Static } from "@sinclair/typebox";

/** Push notifications (ADR 0009): FCM HTTP v1 for Android, APNs token auth for iOS. */

export const PushPlatform = Type.Union([Type.Literal("apns"), Type.Literal("apns_voip"), Type.Literal("fcm")], { $id: "PushPlatform" });
export type PushPlatform = Static<typeof PushPlatform>;

/** `POST /me/push-tokens` — upsert by token (a token moving to another account moves with it). */
export const PushTokenInput = Type.Object(
  {
    app: Type.Union([Type.Literal("rider"), Type.Literal("driver")]),
    platform: PushPlatform,
    token: Type.String({ minLength: 8, maxLength: 4096 }),
    /** APNs only: development builds register against the sandbox. Default production. */
    environment: Type.Optional(Type.Union([Type.Literal("sandbox"), Type.Literal("production")])),
  },
  { $id: "PushTokenInput" },
);
export type PushTokenInput = Static<typeof PushTokenInput>;

/** Notification kinds; `type` in the payload, used by the apps to deep-link. */
export const PUSH_TYPES = [
  "trip.offered", "trip.status_changed", "trip.safety_alert", "chat.message", "support.message", "payout.updated", "driver.review_decided",
  "payment.card_charged", "payment.card_failed", "tip.received", "trip.scheduled_reminder", "safety.incident_updated",
] as const;

/**
 * The data every push carries (APNs: top-level custom keys next to `aps`; FCM: `message.data`,
 * all values strings). `eventId` is the realtime event id: the apps drop a push whose event they
 * already handled over the WebSocket.
 */
export const PushPayload = Type.Object(
  {
    type: Type.String(),
    eventId: Type.String(),
    tripId: Type.Optional(Type.String()),
    ticketId: Type.Optional(Type.String()),
    offerId: Type.Optional(Type.String()),
    title: Type.String(),
    body: Type.String(),
  },
  { $id: "PushPayload" },
);
export type PushPayload = Static<typeof PushPayload>;

/** Notificações (bell on Início): one thing the user was told, newest first. `type` is the event's, for deep links. */
export const NotificationView = Type.Object(
  {
    id: Type.String(),
    type: Type.String(),
    title: Type.String(),
    body: Type.String(),
    tripId: Type.Union([Type.String(), Type.Null()]),
    createdAt: Type.String({ format: "date-time" }),
    read: Type.Boolean(),
  },
  { $id: "NotificationView" },
);
export type NotificationView = Static<typeof NotificationView>;

/** `GET /me/notifications`: a page (newest first) and the unread count; `nextBefore` pages back. */
export const NotificationsPage = Type.Object(
  { items: Type.Array(NotificationView), unread: Type.Integer(), nextBefore: Type.Union([Type.String({ format: "date-time" }), Type.Null()]) },
  { $id: "NotificationsPage" },
);
